import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TenantService } from '../../../core/tenant/tenant.service';
import type { InboxItem, InboxItemTypeFilter, InboxReadStateFilter } from '../models/inbox.models';
import { InboxApiService } from '../services/inbox-api.service';

@Injectable({ providedIn: 'root' })
export class InboxStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(InboxApiService);

  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly itemsSignal = signal<InboxItem[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadingMoreSignal = signal(false);
  private readonly markingAllSignal = signal(false);
  private readonly actionLoadingMapSignal = signal<Record<string, boolean>>({});
  private readonly errorSignal = signal<string | null>(null);
  private readonly pageSignal = signal(1);
  private readonly pageSizeSignal = signal(20);
  private readonly totalCountSignal = signal(0);
  private readonly unreadCountSignal = signal(0);
  private readonly selectedReadFilterSignal = signal<InboxReadStateFilter>('all');
  private readonly selectedTypeFilterSignal = signal<InboxItemTypeFilter>('all');

  readonly items = this.itemsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingMore = this.loadingMoreSignal.asReadonly();
  readonly markingAll = this.markingAllSignal.asReadonly();
  readonly actionLoadingMap = this.actionLoadingMapSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly page = this.pageSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();
  readonly totalCount = this.totalCountSignal.asReadonly();
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly selectedReadFilter = this.selectedReadFilterSignal.asReadonly();
  readonly selectedTypeFilter = this.selectedTypeFilterSignal.asReadonly();
  // aliases para compatibilidade com uso existente em componentes
  readonly selectedFilterReadState = this.selectedReadFilter;
  readonly selectedType = this.selectedTypeFilter;

  readonly hasMore = computed(() => this.items().length < this.totalCount());
  readonly isEmpty = computed(() => !this.loading() && !this.error() && this.items().length === 0);
  readonly hasUnread = computed(() => this.unreadCount() > 0);
  readonly displayedItems = computed(() => this.items());

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        return;
      }

      if (this.loadedCompanyIdSignal() === companyId) return;
      this.loadedCompanyIdSignal.set(companyId);
      this.reset();
      void this.loadUnreadCount();
    });
  }

  async loadInbox(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.pageSignal.set(1);

    try {
      const result = await this.api.getInbox({
        page: 1,
        pageSize: this.pageSize(),
        readState: this.selectedReadFilter(),
        type: this.selectedTypeFilter(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.set(result.items);
      this.totalCountSignal.set(result.totalCount);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.errorSignal.set('inbox.states.error');
      this.itemsSignal.set([]);
      this.totalCountSignal.set(0);
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingSignal.set(false);
    }
  }

  async loadUnreadCount(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    try {
      const result = await this.api.getUnreadCount();
      if (!this.isSameCompany(requestCompanyId)) return;
      this.unreadCountSignal.set(result.count);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.unreadCountSignal.set(0);
    }
  }

  async reloadInbox(): Promise<void> {
    await Promise.all([this.loadInbox(), this.loadUnreadCount()]);
  }

  async markAsRead(id: string): Promise<void> {
    if (this.actionLoadingMap()[id]) return;
    this.setActionLoading(id, true);
    try {
      await this.api.markAsRead(id);
      this.itemsSignal.update((current) =>
        current.map((item) => (item.id === id ? { ...item, isRead: true } : item))
      );
      this.unreadCountSignal.update((current) => Math.max(current - 1, 0));
    } catch {
      this.errorSignal.set('inbox.actions.markReadError');
    } finally {
      this.setActionLoading(id, false);
    }
  }

  async markAsUnread(id: string): Promise<void> {
    if (this.actionLoadingMap()[id]) return;
    this.setActionLoading(id, true);
    try {
      await this.api.markAsUnread(id);
      this.itemsSignal.update((current) =>
        current.map((item) => (item.id === id ? { ...item, isRead: false } : item))
      );
      this.unreadCountSignal.update((current) => current + 1);
    } catch {
      this.errorSignal.set('inbox.actions.markUnreadError');
    } finally {
      this.setActionLoading(id, false);
    }
  }

  async markAllAsRead(): Promise<void> {
    if (this.markingAll()) return;
    this.markingAllSignal.set(true);
    try {
      await this.api.markAllAsRead();
      this.itemsSignal.update((current) => current.map((item) => ({ ...item, isRead: true })));
      this.unreadCountSignal.set(0);
    } catch {
      this.errorSignal.set('inbox.actions.markAllReadError');
    } finally {
      this.markingAllSignal.set(false);
    }
  }

  async changeReadFilter(readState: InboxReadStateFilter): Promise<void> {
    if (this.selectedReadFilter() === readState) return;
    this.selectedReadFilterSignal.set(readState);
    await this.loadInbox();
  }

  async changeTypeFilter(type: InboxItemTypeFilter): Promise<void> {
    if (this.selectedTypeFilter() === type) return;
    this.selectedTypeFilterSignal.set(type);
    await this.loadInbox();
  }

  async loadNextPage(): Promise<void> {
    if (!this.hasMore() || this.loadingMore()) return;
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingMoreSignal.set(true);
    const nextPage = this.page() + 1;
    try {
      const result = await this.api.getInbox({
        page: nextPage,
        pageSize: this.pageSize(),
        readState: this.selectedReadFilter(),
        type: this.selectedTypeFilter(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.update((current) => [...current, ...result.items]);
      this.pageSignal.set(nextPage);
      this.totalCountSignal.set(result.totalCount);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.errorSignal.set('inbox.states.errorLoadMore');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingMoreSignal.set(false);
    }
  }

  reset(): void {
    this.itemsSignal.set([]);
    this.loadingSignal.set(false);
    this.loadingMoreSignal.set(false);
    this.markingAllSignal.set(false);
    this.actionLoadingMapSignal.set({});
    this.errorSignal.set(null);
    this.pageSignal.set(1);
    this.pageSizeSignal.set(20);
    this.totalCountSignal.set(0);
    this.unreadCountSignal.set(0);
    this.selectedReadFilterSignal.set('all');
    this.selectedTypeFilterSignal.set('all');
  }

  private setActionLoading(id: string, loading: boolean): void {
    this.actionLoadingMapSignal.update((current) => ({
      ...current,
      [id]: loading,
    }));
  }

  private isSameCompany(requestCompanyId: string): boolean {
    return this.tenant.companyId() === requestCompanyId;
  }
}
